import _get from 'lodash/get'
import {
  post as apiPost,
  get as apiGet,
  TYPE_GRAPHQL,
  TYPE_BROOKLYN,
} from '../../api-client/index'
import { get as getConfig } from '../../config/index'
import { createAuthForRequest } from '../auth/index'
import { log as logError } from '../../log/error'

const PATH_INFO_QUERY = `
  query PathInfo($path: String!, $filterSet: String, $language: [String]) {
    pathInfo(path: $path, filterSet: $filterSet, language: $language) {
      success
      type
      id
      vocabularyId
      vocabulary
      contentType
      filterSet
      filter
      redirectType
      url
      absolute
      path
      meta {
        title
        description
        keywords
        canonicalUrl
        robotsNoIndex
        robotsNoFollow
        ogTitle
        ogDescription
        ogImage
        ogImageWidth
        ogImageHeight
        ogType
        ogUrl
        twitterTitle
        twitterDescription
        twitterImage
      }
    }
  }
`

/**
 * Map GraphQL pathInfo to the REST pathinfo body shape getRouteInfo expects.
 * @param {Object|null} pathInfo GraphQL pathInfo payload
 * @returns {Object} REST-shaped pathinfo body
 */
export function mapGraphQLPathInfoToRest (pathInfo) {
  if (!pathInfo) {
    return { success: false }
  }
  let { type } = pathInfo
  if (type === 'unknown') {
    type = undefined
  }
  if (type === 'redirect') {
    return {
      success: pathInfo.success,
      type: 'redirect',
      redirectType: pathInfo.redirectType,
      url: pathInfo.url,
      absolute: pathInfo.absolute,
    }
  }
  return {
    success: pathInfo.success,
    type,
    id: pathInfo.id,
    vocabularyId: pathInfo.vocabularyId,
    vocabulary: pathInfo.vocabulary,
    content_type: pathInfo.contentType || null,
    filterSet: pathInfo.filterSet || null,
    filter: pathInfo.filter || null,
    redirectType: pathInfo.redirectType || null,
    url: pathInfo.url || null,
    meta: pathInfo.meta || null,
  }
}

function getGraphqlUrl () {
  const config = getConfig()
  return `${_get(config, 'servers.graphql', '')}graphql`
}

/**
 * Fetch pathinfo via Brooklyn GraphQL, falling back to REST pathinfo on failure.
 * @param {Object} options
 * @param {String} options.path Clean path (no leading slash)
 * @param {Object} [options.pathInfoParams] filterSet, language
 * @param {Object} [options.auth] Redux auth slice
 * @returns {Promise<Object>} REST-shaped pathinfo body
 */
export async function fetchPathInfo ({ path, pathInfoParams = {}, auth }) {
  const variables = {
    path,
    filterSet: pathInfoParams.filterSet || undefined,
    language: pathInfoParams.language || undefined,
  }
  try {
    const res = await apiPost(
      getGraphqlUrl(),
      { query: PATH_INFO_QUERY, variables },
      {
        auth: createAuthForRequest({ auth, includeJwt: false }),
        timeout: 10000,
        maxAttempts: 1,
      },
      TYPE_GRAPHQL,
    )
    const errors = _get(res, ['body', 'errors'], [])
    if (errors.length) {
      throw new Error(_get(errors, [0, 'message'], 'GraphQL pathInfo failed'))
    }
    const pathInfo = _get(res, ['body', 'data', 'pathInfo'])
    return mapGraphQLPathInfoToRest(pathInfo)
  } catch (e) {
    logError(e, `GraphQL pathInfo failed for ${path}; falling back to REST`)
    const res = await apiGet(
      `pathinfo?path=${path}`,
      pathInfoParams,
      { auth: createAuthForRequest({ auth, includeJwt: false }) },
      TYPE_BROOKLYN,
    )
    return _get(res, 'body', { success: false })
  }
}
